import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.beauty';
const pagePath = '/articoli/guida-itaobuy-spreadsheet';
const pageUrl = `${siteUrl}${pagePath}`;

export default function GuidaItaobuySpreadsheet() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-24 md:pt-28 bg-background-50">
        <article className="max-w-[900px] mx-auto px-4 md:px-6 pb-20">
          <div className="mb-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer mb-6"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.back_home')}
            </Link>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-block bg-accent-100/70 text-accent-700 text-[11px] px-3 py-1 rounded-full font-label">
                {t('article.category')}
              </span>
              <span className="text-xs text-foreground-400">{t('article.read_time')}</span>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              Itaobuy Spreadsheet: La Guida Completa per Principianti in Italiano (Aggiornata 2026)
            </h1>

            <p className="text-lg text-foreground-600 leading-relaxed mb-8">
              Benvenuto nella guida più completa in italiano su <strong>Itaobuy Spreadsheet</strong>. 
              Se stai cercando un metodo organizzato e affidabile per trovare prodotti selezionati, 
              confrontare prezzi e fare acquisti in modo intelligente, sei nel posto giusto. In questo 
              articolo approfondito, esploreremo tutto ciò che devi sapere su Itaobuy Spreadsheet: 
              cos&apos;è, come funziona, perché è diventato uno strumento indispensabile per migliaia 
              di acquirenti italiani e come puoi utilizzarlo al meglio per ottenere i prodotti migliori 
              al prezzo più conveniente.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20minimal%20workspace%20with%20laptop%20displaying%20organized%20spreadsheet%20data%20on%20warm%20cream%20desk%2C%20soft%20natural%20daylight%20streaming%20from%20window%2C%20coffee%20cup%20and%20notebook%20beside%20computer%2C%20editorial%20photography%20style%2C%20gentle%20shadows%2C%20neutral%20warm%20tones%2C%20modern%20productivity%20aesthetic%2C%20Italian%20lifestyle%20workspace%20vibe%2C%20professional%20setup&width=1600&height=800&seq=article-hero-guide&orientation=landscape"
              alt="Guida Itaobuy Spreadsheet italiano"
              className="w-full object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Cos&apos;è Itaobuy Spreadsheet?
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                <strong>Itaobuy Spreadsheet</strong> è uno strumento digitale organizzato in formato 
                tabellare che raccoglie e cataloga centinaia di prodotti selezionati, permettendo agli 
                utenti di navigare rapidamente tra diverse categorie merceologiche, confrontare 
                informazioni dettagliate e scegliere articoli con maggiore consapevolezza e sicurezza.
              </p>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                Nato dalla necessità di semplificare il processo di ricerca e acquisto online, 
                Itaobuy Spreadsheet si è evoluto fino a diventare un punto di riferimento per chiunque 
                voglia accedere a un catalogo curato di prodotti verificati, completo di immagini QC 
                (Quality Check), valutazioni degli utenti, fasce di prezzo e link diretti per 
                l&apos;acquisto.
              </p>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                A differenza della ricerca manuale su piattaforme generiche, che può richiedere ore 
                di navigazione e confronto, <strong>Itaobuy Spreadsheet</strong> offre un&apos;esperienza 
                centralizzata e ottimizzata: tutto ciò di cui hai bisogno è organizzato in un unico 
                documento, costantemente aggiornato dal team di Itaobuy con nuovi prodotti, nuove 
                categorie e informazioni sempre fresche.
              </p>
              <p className="text-base text-foreground-600 leading-relaxed">
                Lo spreadsheet è accessibile attraverso il sito principale e rappresenta la risorsa 
                ideale sia per i principianti che si avvicinano per la prima volta a questo metodo 
                di acquisto, sia per gli utenti esperti che cercano un modo ancora più efficiente 
                per scoprire nuovi prodotti e offerte.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Come Funziona Itaobuy Spreadsheet: Guida Passo Passo
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Utilizzare <strong>Itaobuy Spreadsheet</strong> è sorprendentemente semplice. Ecco una guida 
                dettagliata in cinque passaggi per iniziare subito:
              </p>

              <div className="space-y-6">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center font-label text-sm font-bold text-accent-700 flex-shrink-0">01</span>
                    <div>
                      <h3 className="font-label text-base font-semibold text-foreground-900 mb-2">Cerca il Prodotto</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        Accedi allo spreadsheet e utilizza la navigazione per categorie per trovare 
                        il tipo di prodotto che ti interessa. Le categorie spaziano da scarpe e 
                        abbigliamento fino ad accessori, borse e profumi, coprendo praticamente 
                        ogni esigenza di acquisto.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center font-label text-sm font-bold text-accent-700 flex-shrink-0">02</span>
                    <div>
                      <h3 className="font-label text-base font-semibold text-foreground-900 mb-2">Analizza le Informazioni</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        Ogni prodotto nello spreadsheet include informazioni dettagliate: nome, 
                        descrizione, materiali, taglie disponibili e soprattutto il prezzo. 
                        Prenditi il tempo per leggere tutte le informazioni prima di procedere.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center font-label text-sm font-bold text-accent-700 flex-shrink-0">03</span>
                    <div>
                      <h3 className="font-label text-base font-semibold text-foreground-900 mb-2">Controlla le QC Foto</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        Le immagini QC (Quality Check) sono fotografie reali del prodotto, scattate 
                        prima della spedizione. Questa è una delle funzionalità più importanti di 
                        Itaobuy Spreadsheet: puoi vedere esattamente cosa riceverai, verificando 
                        qualità dei materiali, dettagli, colori e rifiniture.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center font-label text-sm font-bold text-accent-700 flex-shrink-0">04</span>
                    <div>
                      <h3 className="font-label text-base font-semibold text-foreground-900 mb-2">Confronta il Prezzo</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        Lo spreadsheet ti permette di confrontare rapidamente prodotti simili e 
                        i relativi prezzi. Questa funzionalità di comparazione ti aiuta a 
                        identificare le migliori offerte e a fare acquisti più convenienti senza 
                        sacrificare la qualità.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center font-label text-sm font-bold text-accent-700 flex-shrink-0">05</span>
                    <div>
                      <h3 className="font-label text-base font-semibold text-foreground-900 mb-2">Completa l&apos;Acquisto</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        Una volta trovato il prodotto perfetto, utilizza i link forniti nello 
                        spreadsheet per procedere all&apos;acquisto in modo sicuro. Tutte le 
                        informazioni necessarie sono a portata di clic.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Perché Usare Itaobuy Spreadsheet?
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Rispetto ad altri metodi di ricerca e acquisto, <strong>Itaobuy Spreadsheet</strong> offre 
                vantaggi significativi in termini di tempo, costo e affidabilità. Ecco un confronto 
                diretto:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse bg-background-50 rounded-xl overflow-hidden border border-background-200/70">
                  <thead>
                    <tr className="bg-accent-100/40">
                      <th className="text-left p-4 font-label text-sm text-foreground-800">Metodo</th>
                      <th className="text-left p-4 font-label text-sm text-foreground-800">Tempo di Ricerca</th>
                      <th className="text-left p-4 font-label text-sm text-foreground-800">Prezzo</th>
                      <th className="text-left p-4 font-label text-sm text-foreground-800">Affidabilità</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-background-200/40">
                      <td className="p-4 text-sm text-foreground-700">Ricerca Manuale</td>
                      <td className="p-4 text-sm text-foreground-700">Alto</td>
                      <td className="p-4 text-sm text-foreground-700">Medio</td>
                      <td className="p-4 text-sm text-foreground-700">Media</td>
                    </tr>
                    <tr className="border-t border-background-200/40">
                      <td className="p-4 text-sm text-foreground-700">Marketplace</td>
                      <td className="p-4 text-sm text-foreground-700">Basso</td>
                      <td className="p-4 text-sm text-foreground-700">Alto</td>
                      <td className="p-4 text-sm text-foreground-700">Alta</td>
                    </tr>
                    <tr className="border-t border-background-200/40">
                      <td className="p-4 text-sm font-semibold text-accent-700">Itaobuy Spreadsheet</td>
                      <td className="p-4 text-sm font-semibold text-accent-700">Basso</td>
                      <td className="p-4 text-sm font-semibold text-accent-700">Ottimo</td>
                      <td className="p-4 text-sm font-semibold text-accent-700">Alta</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Itaobuy Spreadsheet è Sicuro? La Nostra Analisi
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                La sicurezza è una delle domande più frequenti che riceviamo. <strong>Itaobuy Spreadsheet</strong> è 
                progettato con la sicurezza degli utenti come priorità assoluta. Ogni prodotto 
                presente nello spreadsheet è stato verificato e include immagini QC reali, permettendoti 
                di vedere esattamente cosa stai acquistando prima di completare l&apos;ordine.
              </p>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                Ecco alcuni consigli pratici per utilizzare Itaobuy Spreadsheet in totale sicurezza:
              </p>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  <span><strong>Controlla sempre le QC foto</strong>: sono la tua migliore garanzia sulla qualità reale del prodotto.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  <span><strong>Leggi le descrizioni complete</strong>: materiali, taglie e dettagli sono fondamentali per evitare sorprese.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  <span><strong>Confronta i prezzi</strong>: lo spreadsheet ti aiuta a identificare le migliori offerte disponibili.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  <span><strong>Proteggi i tuoi dati personali</strong>: utilizza sempre i canali ufficiali per gli acquisti.</span>
                </li>
              </ul>
              <p className="text-base text-foreground-600 leading-relaxed">
                Seguendo queste semplici linee guida, puoi utilizzare Itaobuy Spreadsheet con la 
                massima tranquillità, sapendo di avere a disposizione uno strumento affidabile e 
                costantemente aggiornato.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Le Categorie Più Popolari su Itaobuy Spreadsheet
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                <strong>Itaobuy Spreadsheet</strong> copre un&apos;ampia gamma di categorie merceologiche. 
                Ecco le categorie più ricercate dagli utenti italiani:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { name: 'Scarpe', desc: 'Sneakers, scarpe sportive e modelli casual delle migliori marche.' },
                  { name: 'Felpe e Maglioni', desc: 'Hoodie, felpe con cappuccio e maglioni per ogni stagione.' },
                  { name: 'T-Shirt', desc: 'T-shirt grafiche, basiche e modelli esclusivi a prezzi competitivi.' },
                  { name: 'Giacche', desc: 'Giubbotti, bomber e giacche tecniche per uomo e donna.' },
                  { name: 'Pantaloni e Shorts', desc: 'Jeans, cargo, pantaloni sportivi e shorts estivi.' },
                  { name: 'Accessori', desc: 'Occhiali, orologi, gioielli e accessori moda.' },
                  { name: 'Borse', desc: 'Zaini, borse a tracolla, tote bag e accessori in pelle.' },
                  { name: 'Profumi', desc: 'Fragranze di tendenza a prezzi convenienti.' },
                ].map((cat) => (
                  <a
                    key={cat.name}
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="bg-background-50 border border-background-200/70 rounded-xl p-4 hover:border-accent-500/30 transition-all cursor-pointer"
                  >
                    <h3 className="font-label text-sm font-semibold text-foreground-900 mb-1">{cat.name}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{cat.desc}</p>
                  </a>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Consigli per Ottenere il Massimo da Itaobuy Spreadsheet
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-accent-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-star-line text-xs text-accent-700"></i>
                  </span>
                  <div>
                    <h3 className="font-label text-sm font-semibold text-foreground-900 mb-1">Controlla regolarmente gli aggiornamenti</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      Lo spreadsheet viene aggiornato frequentemente con nuovi prodotti e offerte. 
                      Visita il sito principale regolarmente per non perdere le ultime novità.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-accent-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-star-line text-xs text-accent-700"></i>
                  </span>
                  <div>
                    <h3 className="font-label text-sm font-semibold text-foreground-900 mb-1">Utilizza i filtri per categoria</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      La navigazione per categorie ti permette di trovare esattamente ciò che cerchi 
                      in pochi secondi, senza dover scorrere prodotti non pertinenti.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-accent-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-star-line text-xs text-accent-700"></i>
                  </span>
                  <div>
                    <h3 className="font-label text-sm font-semibold text-foreground-900 mb-1">Confronta sempre più opzioni</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      Non fermarti al primo prodotto che vedi. Lo spreadsheet è progettato per 
                      facilitare il confronto: prenditi il tempo per valutare diverse alternative.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Domande Frequenti su Itaobuy Spreadsheet
              </h2>

              <div className="space-y-4">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Itaobuy Spreadsheet è disponibile in italiano?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Sì, il nostro portale offre guide complete in italiano e lo spreadsheet è 
                    organizzato con categorie e descrizioni chiare, rendendolo accessibile a tutti 
                    gli utenti italiani, anche ai principianti.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Ogni quanto viene aggiornato Itaobuy Spreadsheet?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Itaobuy Spreadsheet viene aggiornato regolarmente — spesso più volte al mese — 
                    con nuovi prodotti, nuove categorie e informazioni aggiornate su prezzi e 
                    disponibilità. Il team lavora costantemente per mantenere i dati freschi e accurati.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Posso usare Itaobuy Spreadsheet da mobile?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Assolutamente sì. Lo spreadsheet è accessibile da qualsiasi dispositivo, inclusi 
                    smartphone e tablet, permettendoti di cercare prodotti e confrontare prezzi 
                    ovunque ti trovi.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Ci sono costi nascosti nell&apos;usare Itaobuy Spreadsheet?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Itaobuy Spreadsheet è uno strumento trasparente: tutte le informazioni sui 
                    prodotti, inclusi prezzi e dettagli, sono chiaramente indicate. Per informazioni 
                    aggiornate su eventuali costi di accesso, ti invitiamo a visitare il sito 
                    principale.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Quali metodi di pagamento posso utilizzare?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Attraverso il sito principale collegato a Itaobuy Spreadsheet, puoi utilizzare 
                    diversi metodi di pagamento sicuri. Tutte le transazioni sono protette per 
                    garantire la massima sicurezza.
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-accent-100/50 rounded-2xl p-8 md:p-10 text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Pronto a Iniziare con Itaobuy Spreadsheet?
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-8 max-w-lg mx-auto">
                Non perdere altro tempo nella ricerca manuale. Accedi ora a Itaobuy Spreadsheet e 
                scopri il modo più intelligente di fare acquisti online.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-full font-label text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  {t('cta.button_primary')}
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 border border-foreground-300 text-foreground-800 px-8 py-3.5 rounded-full font-label text-sm hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all cursor-pointer whitespace-nowrap"
                >
                  {t('cta.button_secondary')}
                </a>
              </div>
            </section>

            <div className="border-t border-background-200/70 pt-8">
              <p className="text-sm text-foreground-400 mb-4">
                <i className="ri-links-line mr-1"></i>
                Link interni:
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/" className="text-sm text-accent-700 hover:text-accent-500 underline cursor-pointer">
                  Torna alla Homepage di Itaobuy Spreadsheet
                </Link>
                <span className="text-foreground-300">|</span>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer">
                  Visita il sito principale
                </a>
              </div>
            </div>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: 'Itaobuy Spreadsheet: La Guida Completa per Principianti in Italiano (Aggiornata 2026)',
              description: "Guida completa in italiano su Itaobuy Spreadsheet. Scopri cos'è, come funziona, perché usarlo e come fare acquisti sicuri con prodotti verificati e immagini QC.",
              author: { '@type': 'Organization', name: 'Itaobuy Spreadsheet Italia' },
              datePublished: '2026-07-14',
              dateModified: '2026-07-14',
              mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
              publisher: { '@type': 'Organization', name: 'Itaobuy Spreadsheet Italia', url: siteUrl, logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg' },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
                { '@type': 'ListItem', position: 2, name: 'Guida Itaobuy Spreadsheet', item: pageUrl },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}